import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to dynamically set the canonical URL for the current page
 * This helps search engines understand the preferred URL for the page content
 *
 * @param {string} customUrl - Optional custom canonical URL (overrides automatic generation)
 *
 * @example
 * // Automatic canonical URL based on current route
 * useCanonicalUrl();
 *
 * @example
 * // Custom canonical URL
 * useCanonicalUrl('https://remote-job.ai/special-page');
 */
const useCanonicalUrl = (customUrl = null) => {
  const location = useLocation();

  useEffect(() => {
    // Determine the canonical URL
    const baseUrl = 'https://remote-job.ai';
    const canonicalUrl = customUrl || `${baseUrl}${location.pathname}`;

    // Find or create the canonical link element
    let linkElement = document.querySelector('link[rel="canonical"]');

    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      document.head.appendChild(linkElement);
    }

    // Set the canonical URL
    linkElement.setAttribute('href', canonicalUrl);

    console.log('[useCanonicalUrl] Canonical URL set:', canonicalUrl);

    // Cleanup function
    return () => {
      // Canonical tags are typically left in place for navigation
      // They will be updated by the next page that uses this hook
    };
  }, [location.pathname, customUrl]);
};

export default useCanonicalUrl;
